import type { Catalogue, CatalogueEntry, CatalogueRoot } from "../catalogue/types";
import { catalogueEntryOf, catalogueKey, isLeaf, layerId, leavesOf } from "../catalogue/types";
import type { LayerSnapshot } from "../core/store";

export type TreeState = "all" | "some" | "none";

export type TreeRow = {
  key: string;
  collapseKey: string;
  id: string;
  entry: CatalogueEntry;
  level: number;
  isGroup: boolean;
  expanded: boolean;
  snapshot: LayerSnapshot | null;
  state: TreeState;
  single: boolean;
  selectedCount: number;
  totalCount: number;
  loading: boolean;
  error: boolean;
};

export type TreeScope = CatalogueRoot | "all";

export type TreeInput = {
  entries: CatalogueEntry[];
  root: TreeScope;
  snapshots: LayerSnapshot[];
  collapsed: string[];
  filter: string;
  byKey?: Map<string, CatalogueEntry> | null;
};

export function normaliseFilter(filter: string): string {
  return filter.trim().toLocaleLowerCase();
}

export function entryMatches(entry: CatalogueEntry, term: string): boolean {
  if (term.length === 0) return true;

  const haystack = [entry.name, entry.key, entry.idName, ...entry.pathNames].join(" ").toLocaleLowerCase();

  return haystack.includes(term);
}

export function isSingleSelect(entry: CatalogueEntry, byKey: Map<string, CatalogueEntry> | null | undefined): boolean {
  if (!isLeaf(entry) && entry.selection === "single") return true;

  let parentKey = entry.parentKey;
  let guard = 0;

  while (parentKey !== null && guard < 64) {
    const parent = byKey?.get(catalogueKey(entry.root, parentKey)) ?? null;

    if (parent === null) return isLeaf(entry) && entry.selection === "single";
    if (parent.selection === "single") return true;

    parentKey = parent.parentKey;
    guard++;
  }

  return false;
}

export function snapshotIndex(snapshots: LayerSnapshot[]): Map<string, LayerSnapshot> {
  return new Map(snapshots.map(a => [a.id, a]));
}

function buildKeyIndex(entries: CatalogueEntry[], existing: Map<string, CatalogueEntry> | null | undefined): Map<string, CatalogueEntry> {
  if (existing) return existing;

  const map = new Map<string, CatalogueEntry>();
  const visit = (list: CatalogueEntry[]) => {
    for (const entry of list) {
      map.set(catalogueKey(entry.root, entry.key), entry);
      visit(entry.children);
    }
  };

  visit(entries);

  return map;
}

export function groupStateOf(entry: CatalogueEntry, byId: Map<string, LayerSnapshot>): { state: TreeState; selected: number; total: number } {
  const leaves = leavesOf(entry).filter(a => a.visible);
  let selected = 0;
  let total = 0;

  for (const leaf of leaves) {
    const snapshot = byId.get(layerId(leaf.root, leaf.key));

    if (snapshot === undefined) continue;

    total++;

    if (snapshot.selected) selected++;
  }

  const state: TreeState = total > 0 && selected === total ? "all" : selected > 0 ? "some" : "none";

  return { state, selected, total };
}

export function buildTreeRows(input: TreeInput): TreeRow[] {
  const term = normaliseFilter(input.filter);
  const byId = snapshotIndex(input.snapshots);
  const byKey = buildKeyIndex(input.entries, input.byKey);
  const collapsed = new Set(input.collapsed);
  const rows: TreeRow[] = [];

  const visit = (entry: CatalogueEntry, level: number): boolean => {
    if (!entry.visible || (input.root !== "all" && entry.root !== input.root)) return false;

    if (isLeaf(entry)) {
      const snapshot = byId.get(layerId(entry.root, entry.key)) ?? null;

      if (snapshot === null || !entryMatches(entry, term)) return false;

      rows.push({
        key: entry.key,
        collapseKey: catalogueKey(entry.root, entry.key),
        id: snapshot.id,
        entry,
        level,
        isGroup: false,
        expanded: false,
        snapshot,
        state: snapshot.selected ? "all" : "none",
        single: isSingleSelect(entry, byKey),
        selectedCount: snapshot.selected ? 1 : 0,
        totalCount: 1,
        loading: snapshot.status === "loading",
        error: snapshot.status === "error"
      });

      return true;
    }

    const expanded = term.length > 0 || !collapsed.has(`${entry.root}:${entry.key}`);
    const index = rows.length;
    const { state, selected, total } = groupStateOf(entry, byId);
    const placeholder: TreeRow = {
      key: entry.key,
      collapseKey: catalogueKey(entry.root, entry.key),
      id: layerId(entry.root, entry.key),
      entry,
      level,
      isGroup: true,
      expanded,
      snapshot: null,
      state,
      single: isSingleSelect(entry, byKey),
      selectedCount: selected,
      totalCount: total,
      loading: false,
      error: false
    };

    rows.push(placeholder);

    let anyChild = false;

    for (const child of entry.children) {
      const shown = visit(child, level + 1);

      anyChild = anyChild || shown;
    }

    if (!anyChild) {
      rows.splice(index);
      return false;
    }

    if (!expanded) rows.splice(index + 1);

    const leaves = leavesOf(entry).map(a => byId.get(layerId(a.root, a.key))).filter((a): a is LayerSnapshot => a !== undefined);

    placeholder.loading = leaves.some(a => a.selected && a.status === "loading");
    placeholder.error = leaves.some(a => a.selected && a.status === "error");

    return true;
  };

  for (const entry of input.entries) visit(entry, 1);

  return rows;
}

export function rowsForRoot(catalogue: Catalogue | null, root: TreeScope): CatalogueEntry[] {
  if (catalogue === null) return [];
  if (root === "all") return [...catalogue.maps, ...catalogue.layers];

  return root === "maps" ? catalogue.maps : catalogue.layers;
}

export function moveIndex(rows: TreeRow[], index: number, delta: number): number {
  if (rows.length === 0) return -1;
  if (index < 0) return delta > 0 ? 0 : rows.length - 1;

  return Math.min(rows.length - 1, Math.max(0, index + delta));
}

export function typeaheadIndex(rows: TreeRow[], from: number, text: string): number {
  const needle = text.toLocaleLowerCase();

  if (needle.length === 0 || rows.length === 0) return -1;

  for (let step = 1; step <= rows.length; step++) {
    const index = (from + step) % rows.length;

    if (rows[index].entry.name.toLocaleLowerCase().startsWith(needle)) return index;
  }

  return -1;
}

export function parentIndex(rows: TreeRow[], index: number): number {
  const level = rows[index]?.level ?? 0;

  for (let cursor = index - 1; cursor >= 0; cursor--) if (rows[cursor].level < level) return cursor;

  return -1;
}

export function breadcrumb(entry: CatalogueEntry): string {
  return [...entry.pathNames, entry.name].filter(a => a.length > 0).join(" › ");
}

export function layerTitle(catalogue: Catalogue | null, snapshots: LayerSnapshot[], layerName: string | null | undefined): string | null {
  if (!layerName) return null;

  const leaves = (catalogue?.entries ?? []).filter(isLeaf);
  const entry = leaves.find(a => a.idName === layerName) ?? leaves.find(a => layerId(a.root, a.key) === layerName) ?? leaves.find(a => a.key === layerName) ?? null;
  const title = entry !== null ? entry.name : (snapshots.find(a => a.id === layerName)?.title ?? "");

  return title.trim().length > 0 ? title : null;
}

export function selectedOverlayCount(snapshots: LayerSnapshot[], catalogue: Catalogue | null): number {
  return snapshots.filter(a => {
    if (!a.selected) return false;
    if (a.root === "custom" || a.root === "track") return true;

    const found = catalogueEntryOf(catalogue, a);

    return found === null ? a.root !== "maps" : !isSingleSelect(found, catalogue?.byKey);
  }).length;
}
